import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export default function Catalog({ presetCategory }) {
    const params = useParams();
    const q = useQuery();
    const category = presetCategory || q.get("category") || params.category;
    const subcategory = q.get("subcategory");
    const search = q.get("search");
    const featured = q.get("featured") === "true";
    const trending = q.get("trending") === "true";
    const newArrival = q.get("new_arrival") === "true";
    const collection = q.get("collection");

    const [products, setProducts] = useState([]);
    const [sort, setSort] = useState("newest");
    const [priceRange, setPriceRange] = useState([0, 2000000]);
    const [purity, setPurity] = useState("");
    const [showFilter, setShowFilter] = useState(false);
    const [loading, setLoading] = useState(true);

    const title = useMemo(() => {
        if (search) return `Search: "${search}"`;
        if (subcategory) return subcategory.charAt(0).toUpperCase() + subcategory.slice(1);
        if (category) return `${category.charAt(0).toUpperCase() + category.slice(1)} Jewellery`;
        if (featured) return "Featured";
        if (trending) return "Trending";
        if (newArrival) return "New Arrivals";
        return "All Jewellery";
    }, [category, subcategory, search, featured, trending, newArrival]);

    useEffect(() => {
        setLoading(true);
        const p = new URLSearchParams();
        if (category) p.set("category", category);
        if (subcategory) p.set("subcategory", subcategory);
        if (search) p.set("search", search);
        if (featured) p.set("featured", "true");
        if (trending) p.set("trending", "true");
        if (newArrival) p.set("new_arrival", "true");
        if (collection) p.set("collection", collection);
        p.set("sort", sort);
        client.get(`/products?${p.toString()}`).then(r => {
            let list = r.data;
            if (priceRange[0] > 0 || priceRange[1] < 2000000)
                list = list.filter(x => (x.price || 0) >= priceRange[0] && (x.price || 0) <= priceRange[1]);
            if (purity) list = list.filter(x => x.purity === purity);
            setProducts(list);
            setLoading(false);
        });
    }, [category, subcategory, search, featured, trending, newArrival, collection, sort, priceRange, purity]);

    const purities = ["22K", "20K", "18K", "925", "999"];

    return (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
            <div className="mb-10">
                <div className="eyebrow">Jewellery</div>
                <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3" data-testid="catalog-title">{title}</h1>
                <div className="w-14 h-[1px] bg-[color:var(--champagne)] mt-4" />
                <div className="text-sm text-[color:var(--muted-brown)] mt-3">{products.length} pieces</div>
            </div>

            <div className="grid lg:grid-cols-[240px_1fr] gap-8">
                <aside className={`${showFilter ? "fixed inset-0 z-50 bg-[color:var(--ivory)] p-6 overflow-y-auto" : "hidden lg:block"}`}>
                    <div className="flex items-center justify-between mb-6 lg:mb-4">
                        <div className="eyebrow">Refine</div>
                        <button className="lg:hidden" onClick={() => setShowFilter(false)}><X size={20} /></button>
                    </div>

                    <div className="mb-6">
                        <div className="text-xs font-semibold tracking-[0.15em] uppercase mb-3">Sort</div>
                        <select value={sort} onChange={e => setSort(e.target.value)} data-testid="sort-select"
                            className="w-full bg-transparent border border-[color:var(--soft-border)] px-3 py-2 text-sm">
                            <option value="newest">Newest</option>
                            <option value="featured">Featured</option>
                            <option value="price_asc">Price: Low to High</option>
                            <option value="price_desc">Price: High to Low</option>
                        </select>
                    </div>

                    <div className="mb-6">
                        <div className="text-xs font-semibold tracking-[0.15em] uppercase mb-3">Purity</div>
                        <div className="space-y-2">
                            <button onClick={() => setPurity("")} className={`block text-sm ${!purity ? "text-[color:var(--champagne)]" : "text-[color:var(--charcoal)]"}`}>All</button>
                            {purities.map(p => (
                                <button key={p} onClick={() => setPurity(p)} data-testid={`purity-${p}`} className={`block text-sm ${purity === p ? "text-[color:var(--champagne)]" : "text-[color:var(--charcoal)]"}`}>{p}</button>
                            ))}
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="text-xs font-semibold tracking-[0.15em] uppercase mb-3">Price Range</div>
                        <div className="flex items-center gap-2">
                            <input type="number" value={priceRange[0]} onChange={e => setPriceRange([+e.target.value, priceRange[1]])} className="w-full border border-[color:var(--soft-border)] px-2 py-1.5 text-sm bg-transparent" placeholder="Min" />
                            <input type="number" value={priceRange[1]} onChange={e => setPriceRange([priceRange[0], +e.target.value])} className="w-full border border-[color:var(--soft-border)] px-2 py-1.5 text-sm bg-transparent" placeholder="Max" />
                        </div>
                    </div>
                </aside>

                <div>
                    <button className="lg:hidden btn-outline mb-4 py-2" onClick={() => setShowFilter(true)} data-testid="mobile-filter-btn"><SlidersHorizontal size={14} /> Filter & Sort</button>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                            {[...Array(8)].map((_, i) => <div key={i} className="aspect-[4/5] bg-[color:var(--beige)]/50 animate-pulse" />)}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="py-20 text-center">
                            <div className="font-serif-luxe text-2xl mb-2">No pieces found</div>
                            <div className="text-sm text-[color:var(--muted-brown)]">Try adjusting your filters or explore other categories.</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
                            {products.map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
